'use client';
import { useMemo } from 'react';
import { alpha, Box, Button, Theme } from '@mui/material';

import { resolveOutcomeData } from '@/utils/marketUtils';
import type { CardVariantProps } from './types';

// Big Yes/No pair — same treatment on every breakpoint, no separate mobile
// layout. Sizing/typography matches TradeButtons' big Yes/No pair so every
// "two large buttons" card in the app looks the same.
const getBigButtonStyles = (theme: Theme, isYes: boolean) => {
  const variant = isYes ? theme.palette.success : theme.palette.error;
  return {
    py: { xs: 1.125, sm: 0.875 },
    flex: 1,
    minWidth: 0,
    borderRadius: '9px',
    textTransform: 'none' as const,
    fontFamily: 'var(--font-archivo), Archivo, sans-serif',
    fontWeight: 600,
    fontSize: { xs: '14.5px', sm: '13.5px' },
    bgcolor: variant.bg,
    color: theme.palette.mode === 'dark' ? variant.light : variant.dark,
    border: '1px solid',
    borderColor: variant.border,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha(variant.main, 0.25),
      boxShadow: 'none',
    },
  };
};

export function SimpleBinaryCard({ market, outcomes, onTradeClick }: CardVariantProps) {
  const { outcomeNameYes, outcomeNameNo } = useMemo(() => resolveOutcomeData(market), [market]);

  function handleYesClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    let outcomeId = `${market.id}-0`;
    if (Array.isArray(market.outcomes) && market.outcomes[0]) {
      outcomeId = market.outcomes[0].id;
    }

    onTradeClick?.(market.id, outcomeId, true);
  }

  function handleNoClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    let outcomeId = `${market.id}-1`;
    if (Array.isArray(market.outcomes) && market.outcomes[1]) {
      outcomeId = market.outcomes[1].id;
    }

    onTradeClick?.(market.id, outcomeId, true);
  }

  const yesLabel = outcomes[0] || outcomeNameYes || 'Yes';
  const noLabel = outcomes[1] || outcomeNameNo || 'No';

  return (
    <Box sx={{ display: 'flex', width: '100%', gap: 1, mt: 'auto' }}>
      <Button onClick={handleYesClick} sx={(theme) => getBigButtonStyles(theme, true)}>
        {yesLabel}
      </Button>
      <Button onClick={handleNoClick} sx={(theme) => getBigButtonStyles(theme, false)}>
        {noLabel}
      </Button>
    </Box>
  );
}
